import { lazy, Suspense } from 'react'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NumberRating from 'components/Common/NumberRating'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelDetailAmenities from './HotelDetailAmenities'
import HotelDetailPricing from './HotelDetailPricing'
import HotelDetailRoomOptions from './HotelDetailRoomOptions'
import HotelDetailBookedRoom from './HotelDetailBookedRoom'
import { ContentWrapper } from './HotelListingDrawer.styles'
import {
  OfferDetailImageGallery,
  OfferDetailDescription,
  OfferDetailReviews,
  OfferDetailMap,
} from '../OfferDetail'
import {
  DRAWER_DETAIL_MOBILE_GALLERY_PREVIEW_COUNT,
  splitDrawerDetailHero,
} from '../drawerDetailHeroLayout'
import type { OfferDetailReview } from '../OfferDetail'
import type { DrawerHotel } from './types'
import type { DrawerHotelBookedRoom } from 'tripPlanner/utils/types'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_ALWAYS_ON,
} from 'constants/offer'
import type { TripItineraryData, TripStop } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'

const DrawerFlashHotelRoomOptions = lazy(
  () => import('./DrawerFlashHotelRoomOptions'),
)
const DrawerBedbankRoomOptions = lazy(
  () => import('./DrawerBedbankRoomOptions'),
)
const DrawerAlwaysOnRoomOptions = lazy(
  () => import('./DrawerAlwaysOnRoomOptions'),
)

interface Props {
  hotel: DrawerHotel
  titleInContent?: string
  showLocationInContent?: boolean
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
  isBooked?: boolean
  orderId?: string
  savedBookedRooms?: Array<DrawerHotelBookedRoom>
  itineraryData?: TripItineraryData
  contextStop?: TripStop
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onAfterAction?: () => void
  checkIn?: string
  checkOut?: string
  initialOccupancies?: Array<App.Occupants>
  selectedPackageId?: string
  selectedRoomTypeId?: string
  selectedRateId?: string
  selectedCheckIn?: string
  selectedCheckOut?: string
  selectedCharterOriginAirportCode?: string
  selectedCharterFareClass?: string
  scrollToRoomOptions?: boolean
  checkoutSource?: App.CheckoutSource
  onEnlargeMap?: () => void
}

function HotelDetailView({
  hotel,
  titleInContent,
  showLocationInContent = true,
  reviews,
  reviewsLoading,
  isBooked,
  orderId,
  savedBookedRooms,
  itineraryData,
  contextStop,
  onSendMessage,
  onAfterAction,
  checkIn,
  checkOut,
  initialOccupancies,
  selectedPackageId,
  selectedRoomTypeId,
  selectedRateId,
  selectedCheckIn,
  selectedCheckOut,
  selectedCharterOriginAirportCode,
  selectedCharterFareClass,
  scrollToRoomOptions,
  checkoutSource,
  onEnlargeMap,
}: Props) {
  const {
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    amenities,
    price,
    isTotal,
    includesTaxesAndFees,
    unavailableReason,
    luxPlusPrice,
    value,
    memberValue,
    discountPercent,
    memberDiscountPercent,
    offerId,
    deeplinkUrl,
    geoLocation,
    saleUnit,
  } = hotel

  const hasDescription = !!description
  const hasRating = rating !== undefined && rating > 0
  const hasAmenities = !!amenities && amenities.length > 0
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const roomOptions = !isBooked ? hotel.roomOptions : undefined
  const hasRoomOptions = !!roomOptions && roomOptions.length > 0
  const resolvedBookedRooms = savedBookedRooms ?? hotel.bookedRooms
  const hasBookedRooms =
    isBooked && !!resolvedBookedRooms && resolvedBookedRooms.length > 0
  // TRP-4388: Always-On used to be folded into isFlashHotel, mounting the flash/LE room-list
  // (public-offer static packages) for a property whose real inventory lives in
  // svc-accommodation — rendering a room list that silently disagreed with the LE offer page's
  // rate-based rows for the same offer. Property mapping (spp_hotel vs unmapped
  // tactical_ao_hotel), not offer type, is the real discriminator — DrawerAlwaysOnRoomOptions
  // resolves that itself, client-side, the same way OfferPage.tsx does.
  const isFlashHotel = !isBooked && hotel.offerType === OFFER_TYPE_HOTEL
  const isAlwaysOnHotel = !isBooked && hotel.offerType === OFFER_TYPE_ALWAYS_ON
  const isBedbankHotel = !isBooked && hotel.offerType === OFFER_TYPE_BED_BANK
  const mobileFullWidthGallery = Boolean(titleInContent)
  const heroGallery = images.length > 0 ? (
    <OfferDetailImageGallery
      images={images}
      mobileFullWidth={mobileFullWidthGallery}
      previewImageCount={
        mobileFullWidthGallery ? DRAWER_DETAIL_MOBILE_GALLERY_PREVIEW_COUNT : undefined
      }
    />
  ) : null
  const { leadingHero, inlineHero } = splitDrawerDetailHero(mobileFullWidthGallery, heroGallery)

  return (
    <>
      {leadingHero}
      <ContentWrapper>
        <Group direction="vertical" gap={24}>
          {inlineHero}

          {(titleInContent || showLocationInContent || isBooked || hasRating) && (
            <Group direction="vertical" gap={12}>
              {titleInContent && <Heading variant="heading2">{titleInContent}</Heading>}
              {(showLocationInContent || isBooked) && (
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  {showLocationInContent && (
                    <BodyText variant="medium" colour="light-foreground-alt">
                      {location}
                    </BodyText>
                  )}
                  {isBooked && <BookedLabel />}
                </Group>
              )}
              {hasRating && (
                <NumberRating
                  rating={rating}
                  total={reviewsTotal}
                  variant="small"
                  squareContainer
                />
              )}
            </Group>
          )}

          {hasDescription && <OfferDetailDescription description={description} />}

          <Divider kind="secondary" />

          <HotelDetailPricing
            price={price}
            luxPlusPrice={luxPlusPrice}
            value={value}
            memberValue={memberValue}
            discountPercent={discountPercent}
            memberDiscountPercent={memberDiscountPercent}
            isTotal={isTotal}
            includesTaxesAndFees={includesTaxesAndFees}
            unavailableReason={unavailableReason}
            offerId={offerId}
            deeplinkUrl={deeplinkUrl}
            saleUnit={saleUnit}
            isBooked={isBooked}
            orderId={orderId}
          />

          {hasAmenities && (
            <>
              <Divider kind="secondary" />
              <HotelDetailAmenities amenities={amenities} />
            </>
          )}

          {hasBookedRooms && (
            <>
              <Divider kind="secondary" />
              <HotelDetailBookedRoom bookedRooms={resolvedBookedRooms} />
            </>
          )}

          {isFlashHotel && (
            <>
              <Divider kind="secondary" />
              <Suspense fallback={<LoadingIndicator inline visible />}>
                <DrawerFlashHotelRoomOptions
                  offerId={offerId}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  hotel={hotel}
                  itineraryData={itineraryData}
                  contextStop={contextStop}
                  onSendMessage={onSendMessage}
                  onAfterAction={onAfterAction}
                  initialOccupancies={initialOccupancies}
                  selectedPackageId={selectedPackageId}
                  selectedRoomTypeId={selectedRoomTypeId}
                  selectedCheckIn={selectedCheckIn}
                  selectedCheckOut={selectedCheckOut}
                  selectedCharterOriginAirportCode={
                    selectedCharterOriginAirportCode
                  }
                  selectedCharterFareClass={selectedCharterFareClass}
                  scrollToRoomOptions={scrollToRoomOptions}
                  checkoutSource={checkoutSource}
                />
              </Suspense>
            </>
          )}

          {isAlwaysOnHotel && (
            <>
              <Divider kind="secondary" />
              <Suspense fallback={<LoadingIndicator inline visible />}>
                <DrawerAlwaysOnRoomOptions
                  offerId={offerId}
                  roomOptions={roomOptions}
                  deeplinkUrl={deeplinkUrl}
                />
              </Suspense>
            </>
          )}

          {isBedbankHotel && (
            <>
              <Divider kind="secondary" />
              <Suspense fallback={<LoadingIndicator inline visible />}>
                <DrawerBedbankRoomOptions
                  offerId={offerId}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  initialOccupancies={initialOccupancies}
                  selectedPackageId={selectedPackageId}
                  selectedRoomTypeId={selectedRoomTypeId}
                  selectedRateId={selectedRateId}
                  selectedCheckIn={selectedCheckIn}
                  selectedCheckOut={selectedCheckOut}
                  hotel={hotel}
                  itineraryData={itineraryData}
                  contextStop={contextStop}
                  onSendMessage={onSendMessage}
                  onAfterAction={onAfterAction}
                  scrollToRoomOptions={scrollToRoomOptions}
                  checkoutSource={checkoutSource}
                />
              </Suspense>
            </>
          )}

          {!isFlashHotel && !isBedbankHotel && !isAlwaysOnHotel && hasRoomOptions && (
            <>
              <Divider kind="secondary" />
              <HotelDetailRoomOptions
                roomOptions={roomOptions}
                deeplinkUrl={deeplinkUrl}
              />
            </>
          )}

          {(hasReviews || reviewsLoading) && (
            <>
              <Divider kind="secondary" />
              {reviewsLoading && !hasReviews && (
                <Group direction="vertical" gap={12}>
                  <LoadingBox height={20} width={120} borderRadius="S" />
                  <LoadingBox height={60} width={320} borderRadius="S" />
                </Group>
              )}
              {hasReviews && (
                <OfferDetailReviews
                  rating={rating}
                  reviewsTotal={reviewsTotal}
                  reviews={reviews}
                />
              )}
            </>
          )}

          {hasGeoLocation && (
            <>
              <Divider kind="secondary" />
              <OfferDetailMap geoLocation={geoLocation} offerName={name} onEnlarge={onEnlargeMap} />
            </>
          )}
        </Group>
      </ContentWrapper>
    </>
  )
}

export default HotelDetailView
